import crypto from 'node:crypto';
import path from 'node:path';
import pc from 'picocolors';
import { ApiClient } from '../client.js';
import { CONSOLE_URL, DEFAULT_PRODUCT_NAME, LOGIN_TIMEOUT_MS } from '../constants.js';
import { adminUrlFromAppConfig } from '../lib/appConfig.js';
import { startLoopbackServer, type LoopbackLogin } from '../lib/loopback.js';
import {
  loadProfiles,
  normalizeAdminUrl,
  upsertProfile,
  type Branding,
} from '../profiles.js';
import { emitJson, fail, human, note, ok, type Io } from '../output.js';

export interface LoginOptions {
  adminUrl?: string;
  company?: string;
  browser: boolean; // commander --no-browser → browser:false
  json?: boolean;
  /** App folder to read the admin URL from (default: current folder). */
  dir?: string;
}

/**
 * Where the browser hand-off starts (One Console P5):
 *  - 'stamp':   we already know the stamp — go straight to its /cli/authorize
 *               (offline-friendly; --admin-url, app.config, saved default).
 *  - 'console': sign in at the central console's /cli/login shim, which picks
 *               or resolves the stamp server-side (optionally pinned by
 *               --company) and redirects to that stamp's /cli/authorize.
 * Either way the stamp itself mints the CLI JWT and hits our loopback.
 */
export type LoginTarget =
  | { kind: 'stamp'; adminUrl: string }
  | { kind: 'console'; company?: string };

export function resolveLoginTarget(
  io: Io,
  options: LoginOptions,
  homedir?: string,
): LoginTarget {
  if (options.adminUrl) {
    try {
      return { kind: 'stamp', adminUrl: normalizeAdminUrl(options.adminUrl) };
    } catch {
      fail(io, `--admin-url "${options.adminUrl}" is not a valid URL.`, 1, 'INVALID_ADMIN_URL');
    }
  }
  // --company: no client-side discovery — the console shim resolves the slug
  // to a stamp server-side (the old control-tower discovery call is gone).
  if (options.company) {
    return { kind: 'console', company: options.company };
  }
  // Inside a downloaded app folder, the app carries its OWN admin URL — sign
  // into THAT stamp. login is the "pick a tenant" moment, so the folder's URL
  // beats any previously-stored default (a new user has no default anyway).
  const dir = path.resolve(process.cwd(), options.dir ?? '.');
  const fromApp = adminUrlFromAppConfig(dir);
  if (fromApp) {
    try {
      return { kind: 'stamp', adminUrl: normalizeAdminUrl(fromApp) };
    } catch {
      /* a malformed app.config URL shouldn't block login — fall through */
    }
  }
  const existing = loadProfiles(homedir);
  if (existing.defaultAdminUrl) return { kind: 'stamp', adminUrl: existing.defaultAdminUrl };
  // Nothing known locally: sign in at the central console, which knows the
  // user's workspaces (this case used to error / attempt CT discovery).
  return { kind: 'console' };
}

/** The URL the browser opens to start the login hand-off. */
export function buildLoginStartUrl(
  target: LoginTarget,
  redirectUri: string,
  state: string,
): URL {
  const url =
    target.kind === 'stamp'
      ? new URL('/cli/authorize', `${target.adminUrl}/`)
      : new URL('/cli/login', `${CONSOLE_URL}/`);
  url.searchParams.set('redirect_uri', redirectUri);
  url.searchParams.set('state', state);
  if (target.kind === 'console' && target.company) {
    url.searchParams.set('company', target.company);
  }
  return url;
}

export async function runLogin(options: LoginOptions): Promise<void> {
  const io: Io = { json: options.json === true, productName: DEFAULT_PRODUCT_NAME };
  const target = resolveLoginTarget(io, options);

  // When the stamp is already known, fetch branding up front so the loopback
  // "you can close this tab" page and all output speak the customer's product
  // name. Non-fatal. (Console flow: the stamp isn't known yet — default
  // Ghosty branding until the callback tells us which stamp signed us in.)
  let branding: Branding = { productName: DEFAULT_PRODUCT_NAME };
  let brandedFor: string | null = null;
  let company = options.company;
  if (target.kind === 'stamp') {
    try {
      const config = await new ApiClient(target.adminUrl).fetchConfig();
      branding = {
        productName: config.productName ?? DEFAULT_PRODUCT_NAME,
        logoUrl: config.logoUrl ?? null,
        colors: config.colors ?? null,
      };
      company = company ?? config.tenantId;
      brandedFor = target.adminUrl;
      io.productName = branding.productName;
    } catch {
      /* keep defaults */
    }
  }

  const state = crypto.randomBytes(16).toString('hex');
  const server = await startLoopbackServer({
    expectedState: state,
    timeoutMs: LOGIN_TIMEOUT_MS,
    productName: io.productName,
  });

  const startUrl = buildLoginStartUrl(target, server.redirectUri, state);

  if (options.browser) {
    note(io, 'Opening your browser to sign in...');
    note(io, `If it doesn't open, visit: ${startUrl}`);
    try {
      const { default: open } = await import('open');
      await open(startUrl.toString());
    } catch {
      // Non-fatal: URL is printed above for manual opening.
    }
  } else if (io.json) {
    emitJson({ event: 'authorize_url', url: startUrl.toString() });
  } else {
    human(io, 'Open this URL in your browser to sign in:');
    human(io, `  ${startUrl}`);
  }

  let login: LoopbackLogin;
  try {
    login = await server.result;
  } catch (err) {
    server.close();
    fail(io, (err as Error).message, 1, 'LOGIN_TIMEOUT');
  }

  // Which stamp signed us in: the callback's admin_url param wins (console
  // flow, and any up-to-date stamp); otherwise fall back to the pre-resolved
  // stamp URL (older stamps don't send the param yet).
  let adminUrl: string | null = target.kind === 'stamp' ? target.adminUrl : null;
  if (login.adminUrl) {
    try {
      adminUrl = normalizeAdminUrl(login.adminUrl);
    } catch {
      /* malformed param — keep the pre-resolved URL when we have one */
    }
  }
  if (!adminUrl) {
    fail(
      io,
      'Signed in, but your workspace did not report its address (it may need a platform ' +
        'update). Run `ghosty login --admin-url <url>` to sign in directly.',
      1,
      'NO_ADMIN_URL',
    );
  }

  // Make sure the cached profile carries THIS stamp's branding: the console
  // flow lands here with none, and the stamp flow retries a transient
  // pre-flow failure.
  if (brandedFor !== adminUrl) {
    try {
      const config = await new ApiClient(adminUrl).fetchConfig();
      branding = {
        productName: config.productName ?? DEFAULT_PRODUCT_NAME,
        logoUrl: config.logoUrl ?? null,
        colors: config.colors ?? null,
      };
      company = company ?? config.tenantId;
    } catch {
      /* keep defaults */
    }
  }

  upsertProfile(adminUrl, {
    email: login.email,
    token: login.token,
    ...(company ? { company } : {}),
    branding,
    createdAt: new Date().toISOString(),
  });

  if (io.json) {
    emitJson({
      ok: true,
      email: login.email,
      adminUrl,
      company: company ?? null,
      productName: branding.productName,
    });
    return;
  }
  ok({ ...io, productName: branding.productName }, `Signed in to ${pc.bold(branding.productName)} as ${pc.bold(login.email)}`);
  note(io, 'Credentials saved to ~/.ghosty/profiles.json');
  human(io, `${pc.green('ready')} — you can now run \`ghosty init\` in a new folder.`);
}
